import { getApiUrl } from './api';
import { isFeatureEnabled } from '../config/environment';

// Generate a simple session ID
const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Get or create session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visitor_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('visitor_session_id', sessionId);
  }
  return sessionId;
};

// Detect potential social media source from URL parameters and other indicators
const detectSocialMediaSource = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const currentUrl = window.location.href;
  
  // Check for common social media UTM parameters
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const fbclid = urlParams.get('fbclid'); // Facebook click ID
  const gclid = urlParams.get('gclid'); // Google click ID
  const liClickId = urlParams.get('li_fat_id'); // LinkedIn click ID
  
  // Check for social media indicators
  if (utmSource) {
    if (utmSource.toLowerCase().includes('linkedin')) return 'linkedin.com';
    if (utmSource.toLowerCase().includes('facebook')) return 'facebook.com';
    if (utmSource.toLowerCase().includes('twitter')) return 'twitter.com';
    if (utmSource.toLowerCase().includes('instagram')) return 'instagram.com';
    if (utmSource.toLowerCase().includes('youtube')) return 'youtube.com';
    if (utmSource.toLowerCase().includes('github')) return 'github.com';
  }
  
  if (utmMedium) {
    if (utmMedium.toLowerCase().includes('social')) {
      // If medium is social but no specific source, try to detect from other params
      if (liClickId) return 'linkedin.com';
      if (fbclid) return 'facebook.com';
    }
  }
  
  // Check for specific click IDs
  if (fbclid) return 'facebook.com';
  if (liClickId) return 'linkedin.com';
  if (gclid) return 'google.com';
  
  return null;
};

// Track page visit
export const trackPageVisit = async (path = window.location.pathname) => {
  try {
    // Check if analytics is enabled
    if (!isFeatureEnabled('ENABLE_ANALYTICS')) {
      return;
    }

    // Don't track admin panel visits to avoid skewing analytics
    if (path.startsWith('/admin')) {
      return;
    }

    // Get referrer from document.referrer or detect from URL parameters
    let referrer = document.referrer || null;
    const detectedSource = detectSocialMediaSource();
    
    // If no referrer but we detected a social media source, use that
    if (!referrer && detectedSource) {
      referrer = `https://${detectedSource}`;
    }

    const visitorData = {
      path: path,
      referrer: referrer,
      userAgent: navigator.userAgent,
      sessionId: getSessionId(),
      timestamp: new Date().toISOString()
    };

    // Send visitor data to Supabase Edge Function
    const response = await fetch(getApiUrl('log-visitor'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(visitorData)
    });

    if (!response.ok) {
      console.warn('Failed to track visitor:', response.statusText);
    }
  } catch (error) {
    // Silently fail to avoid disrupting user experience
    console.warn('Visitor tracking error:', error);
  }
};

// Track page visit with debouncing to avoid duplicate calls
let trackingTimeout;
export const trackPageVisitDebounced = (path = window.location.pathname, delay = 1000) => {
  clearTimeout(trackingTimeout);
  trackingTimeout = setTimeout(() => {
    trackPageVisit(path);
  }, delay);
};

// Initialize visitor tracking
export const initializeVisitorTracking = () => {
  // Track initial page load
  trackPageVisit();

  // Track route changes for SPAs
  let currentPath = window.location.pathname;
  
  // Override pushState and replaceState to track route changes
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function(...args) {
    originalPushState.apply(history, args);
    const newPath = window.location.pathname;
    if (newPath !== currentPath) {
      currentPath = newPath;
      trackPageVisitDebounced(newPath);
    }
  };

  history.replaceState = function(...args) {
    originalReplaceState.apply(history, args);
    const newPath = window.location.pathname;
    if (newPath !== currentPath) {
      currentPath = newPath;
      trackPageVisitDebounced(newPath);
    }
  };

  // Listen for popstate events (back/forward buttons)
  window.addEventListener('popstate', () => {
    const newPath = window.location.pathname;
    if (newPath !== currentPath) {
      currentPath = newPath;
      trackPageVisitDebounced(newPath);
    }
  });

  // Track page visibility changes (when user returns to tab)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      trackPageVisitDebounced(window.location.pathname, 2000);
    }
  });
};

// Analytics API functions for admin panel
export const getVisitorAnalytics = async (token, dateRange = null) => {
  try {
    let url = 'get-visitor-analytics?type=summary';
    if (dateRange && dateRange.start_date && dateRange.end_date) {
      url += `&start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
    }
    
    const response = await fetch(getApiUrl(url), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching visitor analytics:', error);
    throw error;
  }
};

export const getDailyVisitorCounts = async (token, days = 30, dateRange = null) => {
  try {
    let url = `get-visitor-analytics?type=daily&days=${days}`;
    if (dateRange && dateRange.start_date && dateRange.end_date) {
      url += `&start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
    }
    
    const response = await fetch(getApiUrl(url), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching daily visitor counts:', error);
    throw error;
  }
};

export const getTopCountries = async (token, limit = 10, dateRange = null) => {
  try {
    let url = `get-visitor-analytics?type=countries&limit=${limit}`;
    if (dateRange && dateRange.start_date && dateRange.end_date) {
      url += `&start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
    }
    
    const response = await fetch(getApiUrl(url), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching top countries:', error);
    throw error;
  }
};

export const getRecentVisits = async (token, limit = 200, dateRange = null) => {
  try {
    let url = `get-visitor-analytics?type=recent&limit=${limit}`;
  
    if (dateRange && dateRange.start_date && dateRange.end_date) {
      url += `&start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
    
    } else {

    }
  
    
    const response = await fetch(getApiUrl(url), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Data processing can be added here if needed
    if (data && data.data && data.data.length > 0) {
      // Data processing can be added here if needed
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching recent visits:', error);
    throw error;
  }
};